<template>
  <div>
    <h1 class="text-h5">Social Accounts Pending Client Action</h1>
    <clients-loader
      :customer-id="customerId"
      :filters="clientFilters"
      :fields="['client.socialAccounts']"
      @loaded="onClientsLoaded"
    />
    <sites-loader v-model="socialSites" />
    <q-table
      :columns="columns"
      :rows="clients"
      v-model:pagination="pagination"
      v-bind="$attrs"
      @request="pagination = $event.pagination"
    >
      <template v-slot:top>
        <q-input
          v-model="keyword"
          placeholder="Name, email, file ref no."
          clear-icon="fa fa-times"
          clearable
          style="min-width: 320px"
          filled
          dense
          autocomplete="no"
          @clear="keyword = ''">
          <template v-slot:prepend>
            <q-icon name="fa fa-search" />
          </template>
        </q-input>
      </template>
      <template v-slot:body-cell="props">
        <q-td v-if="props.col.name === 'client'" :props="props">
          <router-link
            :to="{name: 'client_profile', params: {clientId: props.row.id}}"
            class="link"
            title="View Profile"
          >
            {{ $filters.clientNameFilter(props.row) }}
          </router-link>
        </q-td>
        <q-td v-else-if="props.col.socialSite" :props="props">
          <view-request-btn
            v-if="props.value && props.value.reconnectRequest"
            :request="props.value.reconnectRequest"
            :platform="props.col.socialSite.name"
          />
          <q-btn-dropdown
            v-else-if="props.value && !props.value.reconnectRequest"
            icon="fa fa-check-circle"
            color="positive"
            rounded
            flat
          >
            <div class="q-pa-md">
              No client action is required.
            </div>
          </q-btn-dropdown>
          <q-btn-dropdown v-else icon="fa fa-times" rounded flat>
            <div class="q-pa-md">
              {{ props.col.socialSite.name }} account was not added by the client.
            </div>
          </q-btn-dropdown>
        </q-td>
        <q-td v-else :props="props">
          {{ props.value }}
        </q-td>
      </template>
    </q-table>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  Client, ClientFilters,
  QTablePagination,
  QueryResult,
  SocialSite,
} from '@/type';
import { name as clientNameFilter } from '@/client/client-filter';
import ClientsLoader from '@/client/ClientsLoader.vue';
import SitesLoader from '@/social/site/SitesLoader.vue';
import eventBus from '@/messaging/event-bus';
import ViewRequestBtn from './ViewRequestBtn.vue';
import {
  ReconnectRequestEvent,
  ReconnectRequestCanceled,
  RemindersDisabled,
  RemindersEnabled,
} from '.';

interface Data {
  clients: Client[],
  socialSites: SocialSite[],
  pagination: QTablePagination,
  keyword: string,
}

interface Column {
  name: string,
  label: string,
  field: string|{(c: Client): any},
  align: string,
  socialSite?: SocialSite,
}

export default defineComponent({
  components: {
    ViewRequestBtn,
    ClientsLoader,
    SitesLoader,
  },
  props: {
    customerId: {
      type: Number,
      required: true,
    },
  },
  data(): Data {
    return {
      clients: [],
      socialSites: [],
      pagination: {
        page: 1,
        rowsPerPage: 10,
        rowsNumber: 0,
      },
      keyword: '',
    };
  },
  computed: {
    clientFilters(): ClientFilters {
      return {
        keyword: this.keyword,
        pendingReconnections: true,
        offset: (this.pagination.page - 1) * this.pagination.rowsPerPage,
        limit: this.pagination.rowsPerPage,
      };
    },
    columns(): Column[] {
      const columns:Column[] = [
        {
          name: 'client',
          label: 'Client',
          field: clientNameFilter,
          align: 'left',
        },
        {
          name: 'fileRefNo',
          label: 'File Ref No.',
          field: 'fileRefNo',
          align: 'left',
        },
      ];
      this.socialSites.forEach((socialSite) => {
        columns.push({
          socialSite,
          name: socialSite.type,
          label: socialSite.name,
          field(client: Client): any {
            if (!Array.isArray(client.socialAccounts)) return null;
            return client.socialAccounts.find(sa => sa.socialSite.id === socialSite.id);
          },
          align: 'center',
        });
      });
      return columns;
    },
  },
  methods: {
    onClientsLoaded(result: QueryResult<Client>): void {
      this.clients = result.items;
      this.pagination.rowsNumber = result.total;
    },
    onRequestCanceled(event: ReconnectRequestCanceled): void {
      const request = event.reconnectRequest;
      for (let i = 0; i < this.clients.length; i += 1) {
        const accounts = this.clients[i].socialAccounts || [];
        const index = accounts.findIndex(sa => sa.id === request.accountId);
        if (index >= 0) {
          const account = { ...accounts[index], reconnectRequest: null };
          accounts.splice(index, 1, account);
          return;
        }
      }
    },
    onReconnectRequestUpdated(event: ReconnectRequestEvent): void {
      const request = event.reconnectRequest;
      for (let i = 0; i < this.clients.length; i += 1) {
        const accounts = this.clients[i].socialAccounts || [];
        const index = accounts.findIndex(sa => sa.id === request.accountId);
        if (index >= 0) {
          const account = { ...accounts[index], reconnectRequest: request };
          accounts.splice(index, 1, account);
          return;
        }
      }
    },
  },
  filters: {
    clientNameFilter,
  },
  created(): void {
    eventBus.$on(ReconnectRequestCanceled.eventName, this.onRequestCanceled);
    eventBus.$on(RemindersDisabled.eventName, this.onReconnectRequestUpdated);
    eventBus.$on(RemindersEnabled.eventName, this.onReconnectRequestUpdated);
  },
  beforeUnmount(): void {
    eventBus.$off(ReconnectRequestCanceled.eventName, this.onRequestCanceled);
    eventBus.$off(RemindersDisabled.eventName, this.onReconnectRequestUpdated);
    eventBus.$off(RemindersEnabled.eventName, this.onReconnectRequestUpdated);
  },
});
</script>
